import { ErrorCode } from 'react-dropzone';

import type { Accept, FileRejection } from 'react-dropzone';
import type { SelectedFile } from './types';

function getFileExtension(fileName: string): string {
  return `.${fileName.split('.').pop()?.toLowerCase() ?? ''}`;
}

function isFileAccepted(file: File, accept: Accept): boolean {
  if (file.type && Object.prototype.hasOwnProperty.call(accept, file.type)) {
    return true;
  }

  const ext = getFileExtension(file.name);
  return Object.values(accept)
    .flat()
    .some((acceptedExt) => acceptedExt.toLowerCase() === ext);
}

/** Copy the File so preview/noPreview metadata never mutates the caller's instance. */
export function toSelectedFile(file: File, noPreviewFiles: string[]): SelectedFile {
  const selected = new File([file], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
  if (noPreviewFiles.includes(getFileExtension(file.name))) {
    return Object.assign(selected, { noPreview: true });
  }
  return Object.assign(selected, { preview: URL.createObjectURL(selected) });
}

export function getFileValidationError(
  file: File,
  accept: Accept,
  maxSizeMb: number,
): string | null {
  if (!isFileAccepted(file, accept)) {
    return `"${file.name}" is not a supported file type. Choose a ${Object.values(accept)
      .flat()
      .join(', ')} file.`;
  }

  if (file.size > maxSizeMb * 1024 * 1024) {
    return `"${file.name}" is larger than the ${maxSizeMb} MB limit.`;
  }

  return null;
}

export function getFileRejectionMessage(
  rejection: FileRejection,
  accept: Accept,
  maxSizeMb: number,
): string {
  const [error] = rejection.errors;

  if (error?.code === ErrorCode.FileInvalidType) {
    return `"${rejection.file.name}" is not a supported file type. Choose a ${Object.values(accept)
      .flat()
      .join(', ')} file.`;
  }

  if (error?.code === ErrorCode.FileTooLarge) {
    return `"${rejection.file.name}" is larger than the ${maxSizeMb} MB limit.`;
  }

  return error?.message ?? `"${rejection.file.name}" could not be selected.`;
}

export function isDuplicateFileName(name: string, existingFileNames: string[]): boolean {
  return existingFileNames.some(
    (existingName) => existingName.trim().toLowerCase() === name.trim().toLowerCase(),
  );
}
